import React from 'react';
import { motion } from 'framer-motion';
import { Film, Camera, CalendarCheck, Instagram, Globe, Palette, Building2 } from 'lucide-react';

/**
 * StudioExperience — "A experiência por trás da COUTIN"
 * Valoriza o que a Coutin já faz hoje (produção, foto, redes, sites) para dar
 * lastro à marca antes da inauguração do estúdio em 2027.
 * Nenhum número/cliente fictício.
 */
const items = [
  { icon: Film, title: 'Produção Audiovisual', desc: 'Captação e edição de vídeos institucionais, comerciais e de campanha.' },
  { icon: Camera, title: 'Fotografia', desc: 'Ensaios profissionais, catálogo, produto, evento e institucional.' },
  { icon: CalendarCheck, title: 'Cobertura de Eventos', desc: 'Registro cinematográfico de eventos corporativos, shows e ações.' },
  { icon: Instagram, title: 'Conteúdo para Redes', desc: 'Estratégia, produção e publicação para Instagram, TikTok e YouTube.' },
  { icon: Globe, title: 'Websites', desc: 'Sites institucionais, landing pages e portais com foco em performance.' },
  { icon: Palette, title: 'Design e Comunicação', desc: 'Identidade visual, materiais gráficos e comunicação de marca.' },
  { icon: Building2, title: 'Projetos para Marcas', desc: 'Operação completa de marketing e produção para empresas e negócios.' },
];

const StudioExperience = () => {
  return (
    <section
      id="experiencia"
      data-testid="studio-experience"
      className="relative py-24 md:py-32 bg-[#050508] border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(245,158,11,0.08)_0%,rgba(0,0,0,0)_55%)]" />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14 md:mb-16 max-w-3xl mx-auto"
        >
          <span className="text-[10px] md:text-xs uppercase tracking-[0.4em] font-bold text-[#F59E0B] mb-4 inline-block">
            Antes do Estúdio
          </span>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-black text-[#F5F5F0] leading-[1.1] mb-6">
            A experiência por trás da{' '}
            <span className="text-[#2540A3] italic">COUTIN</span>.
          </h2>
          <p className="text-base md:text-lg text-[#A1A1AA] font-light leading-relaxed">
            Antes de receber você em nosso novo espaço, já transformamos ideias em
            vídeos, imagens, projetos e experiências para diferentes marcas e
            negócios em Foz do Iguaçu e região.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5, delay: Math.min(i * 0.05, 0.35) }}
                data-testid={`experience-card-${i}`}
                className="group flex gap-4 p-5 md:p-6 rounded-2xl border border-white/8 hover:border-[#F59E0B]/50 bg-gradient-to-br from-[#141419] to-[#050508] transition-all duration-400"
              >
                <div className="w-11 h-11 rounded-xl bg-[#F59E0B]/12 border border-[#F59E0B]/30 flex items-center justify-center shrink-0 group-hover:bg-[#F59E0B]/25 transition-all">
                  <Icon className="w-5 h-5 text-[#F59E0B]" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="font-display text-base md:text-lg font-bold text-[#F5F5F0] mb-1 leading-tight">
                    {it.title}
                  </h3>
                  <p className="text-sm text-[#A1A1AA] font-light leading-relaxed">
                    {it.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StudioExperience;
